import { useCallback, useEffect, useRef, useState } from 'react';
import type { Activity } from '../types/activity';
import { ApiError, apiFetch } from '../utils/api';
import { loadActivitySnapshot } from '../utils/activitySnapshot';
import type { ActivityProgram, ActivityWriteScope } from '../utils/activityPrograms';

interface ActivitySnapshot {
  activities: Activity[];
  capabilities?: ActivityCapabilities;
}

export interface ActivityCapabilities {
  canCreate: boolean;
  canClear?: boolean;
  canImport?: boolean;
  programs?: ActivityProgram[];
  createScopes: Array<{ unitIds: string[]; regions: string[] }>;
  updateScopes?: ActivityWriteScope[];
  writableUnitIds: string[];
  units: Array<{ id: string; name: string; type: string; parentId: string | null }>;
}

interface MutationResult {
  task: Activity;
  version: number;
}

const PATCH_FIELDS: Array<keyof Activity> = [
  'title', 'owningUnitId', 'owningUnit', 'functionType', 'region', 'countryScope', 'owner', 'assignees',
  'startDate', 'dueDate', 'scheduleMode', 'priority', 'impact', 'note', 'impactedPrograms', 'durationDays',
];

function validateActivitySnapshot(value: unknown): ActivitySnapshot {
  if (!value || typeof value !== 'object' || !Array.isArray((value as Partial<ActivitySnapshot>).activities)) {
    throw new ApiError('Activity API returned an invalid snapshot', 502, { code: 'INVALID_ACTIVITY_SNAPSHOT' });
  }
  return value as ActivitySnapshot;
}

function same(left: unknown, right: unknown) {
  return JSON.stringify(left) === JSON.stringify(right);
}

function depthOf(activity: Activity, byId: Map<string, Activity>) {
  let depth = 0;
  let parentId = activity.parentId;
  const visited = new Set<string>();
  while (parentId && !visited.has(parentId)) {
    visited.add(parentId);
    depth += 1;
    parentId = byId.get(parentId)?.parentId;
  }
  return depth;
}

function headers() {
  return { 'Idempotency-Key': `tracker:${crypto.randomUUID()}` };
}

export interface ActivityStore {
  activities: Activity[];
  saveActivities: (activities: Activity[]) => void;
  updateActivity: (id: string, patch: Partial<Activity>) => Promise<void>;
  bulkUpdateActivities: (ids: Iterable<string>, patch: Partial<Activity>) => Promise<void>;
  bulkMoveActivities: (ids: Iterable<string>, direction: 'indent' | 'outdent') => Promise<void>;
  deleteActivities: (ids: Iterable<string>) => Promise<void>;
  clearAllActivities: (expectedRevision: number, idempotencyKey: string) => Promise<{cleared:number;revision:number}>;
  reload: () => Promise<void>;
  loadedAt: Date | null;
  isLoading: boolean;
  isSaving: boolean;
  error: string | null;
  clearError: () => void;
  capabilities: ActivityCapabilities;
}

export function useActivityStore(): ActivityStore {
  const [loadedAt, setLoadedAt] = useState<Date | null>(null);
  const [activities, setActivities] = useState<Activity[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [capabilities, setCapabilities] = useState<ActivityCapabilities>({
    canCreate: false,
    createScopes: [],
    writableUnitIds: [],
    units: [],
  });
  const serverRef = useRef<Activity[]>([]);
  const pendingRef = useRef<Activity[] | null>(null);
  const savingRef = useRef(false);
  const mountedRef = useRef(true);

  const reload = useCallback(async () => {
    setIsLoading(true);
    try {
      const snapshot = validateActivitySnapshot(await loadActivitySnapshot(apiFetch));
      if (!mountedRef.current) return;
      serverRef.current = snapshot.activities;
      setActivities(snapshot.activities);
      setLoadedAt(new Date());
      setCapabilities(snapshot.capabilities || {
        canCreate: false, createScopes: [], writableUnitIds: [], units: [],
      });
      setError(null);
    } catch (requestError) {
      if (mountedRef.current) {
        setCapabilities({canCreate:false,createScopes:[],writableUnitIds:[],units:[]});
        setError(requestError instanceof Error ? requestError.message : 'Unable to load activities');
      }
    } finally {
      if (mountedRef.current) setIsLoading(false);
    }
  }, []);

  const synchronize = useCallback(async (desired: Activity[]) => {
    const desiredById = new Map(desired.map((task) => [task.id, task]));
    const working = new Map(serverRef.current.map((task) => [task.id, task]));
    const desiredDepth = new Map(desired.map((task) => [task.id, depthOf(task, desiredById)]));

    const created = desired
      .filter((task) => !working.has(task.id))
      .sort((left, right) => (desiredDepth.get(left.id) || 0) - (desiredDepth.get(right.id) || 0));
    for (const task of created) {
      const result = await apiFetch('/api/v1/activities', {
        method: 'POST', headers: headers(), body: JSON.stringify({ task: { ...task, status: 'not-started', dependencies: [] } }),
      }) as MutationResult;
      working.set(task.id, result.task);
    }

    for (const desiredTask of desired) {
      let current = working.get(desiredTask.id);
      if (!current) continue;
      const patch: Partial<Activity> = {};
      PATCH_FIELDS.forEach((field) => {
        if (!same(current?.[field], desiredTask[field])) (patch as any)[field] = desiredTask[field];
      });
      if (Object.keys(patch).length) {
        const result = await apiFetch(`/api/v1/activities/${encodeURIComponent(desiredTask.id)}`, {
          method: 'PATCH', headers: headers(), body: JSON.stringify({ expectedVersion: current.version || 1, patch }),
        }) as MutationResult;
        current = result.task;
        working.set(desiredTask.id, current);
      }
      if ((current.parentId ?? null) !== (desiredTask.parentId ?? null) || (current.sortOrder ?? 0) !== (desiredTask.sortOrder ?? 0)) {
        const result = await apiFetch(`/api/v1/activities/${encodeURIComponent(desiredTask.id)}/move`, {
          method: 'POST', headers: headers(), body: JSON.stringify({
            expectedVersion: current.version || 1,
            parentId: desiredTask.parentId ?? null,
            sortOrder: desiredTask.sortOrder ?? 0,
          }),
        }) as MutationResult;
        current = result.task;
        working.set(desiredTask.id, current);
      }
    }

    for (const desiredTask of desired) {
      let current = working.get(desiredTask.id);
      if (!current) continue;
      const currentDeps = new Map(current.dependencies.map((dependency) => [dependency.taskId, dependency]));
      const desiredDeps = new Map(desiredTask.dependencies.map((dependency) => [dependency.taskId, dependency]));
      for (const dependency of current.dependencies) {
        if (desiredDeps.has(dependency.taskId)) continue;
        const result = await apiFetch(`/api/v1/activities/${encodeURIComponent(current.id)}/dependencies/${encodeURIComponent(dependency.taskId)}`, {
          method: 'DELETE', headers: headers(), body: JSON.stringify({ expectedVersion: current.version || 1 }),
        }) as MutationResult;
        current = result.task;
        working.set(current.id, current);
      }
      for (const dependency of desiredTask.dependencies) {
        if (same(currentDeps.get(dependency.taskId), dependency)) continue;
        const result = await apiFetch(`/api/v1/activities/${encodeURIComponent(current.id)}/dependencies/${encodeURIComponent(dependency.taskId)}`, {
          method: 'PUT', headers: headers(), body: JSON.stringify({ expectedVersion: current.version || 1, ...dependency }),
        }) as MutationResult;
        current = result.task;
        working.set(current.id, current);
      }
    }

    const statusChanges = desired
      .filter((task) => working.get(task.id)?.status !== task.status)
      .sort((left, right) => (desiredDepth.get(right.id) || 0) - (desiredDepth.get(left.id) || 0));
    for (const desiredTask of statusChanges) {
      const current = working.get(desiredTask.id)!;
      const result = await apiFetch(`/api/v1/activities/${encodeURIComponent(current.id)}/transitions`, {
        method: 'POST', headers: headers(), body: JSON.stringify({
          expectedVersion: current.version || 1,
          targetStatus: desiredTask.status,
          reason: desiredTask.note || 'Updated from Activity Tracker',
        }),
      }) as MutationResult;
      working.set(current.id, result.task);
    }

    const deletedIds = serverRef.current.filter((task) => !desiredById.has(task.id)).map((task) => task.id);
    const deleted = new Set(deletedIds);
    const roots = deletedIds.filter((id) => {
      let parentId = working.get(id)?.parentId;
      while (parentId) {
        if (deleted.has(parentId)) return false;
        parentId = working.get(parentId)?.parentId;
      }
      return true;
    });
    if (roots.length) {
      await apiFetch('/api/v1/activities/bulk', {
        method: 'POST', headers: headers(), body: JSON.stringify({
          expectedVersions: Object.fromEntries(roots.map((id) => [id, working.get(id)?.version || 1])),
          request: { operation: 'delete', taskIds: roots },
        }),
      });
      deletedIds.forEach((id) => working.delete(id));
    }

    // Retain canonical values and per-record permissions returned by the API.
    // A client draft has no allowedActions until its create request succeeds.
    serverRef.current = desired.map((task) => working.get(task.id)!);
  }, []);

  const persistPending = useCallback(async () => {
    if (savingRef.current) return;
    savingRef.current = true;
    if (mountedRef.current) setIsSaving(true);
    try {
      while (pendingRef.current) {
        const desired = pendingRef.current;
        pendingRef.current = null;
        try {
          await synchronize(desired);
          if (mountedRef.current) {
            setActivities(serverRef.current);
            setError(null);
          }
        } catch (requestError) {
          pendingRef.current = null;
          await reload();
          if (mountedRef.current) {
            const conflict = requestError instanceof ApiError && requestError.status === 409;
            setError(conflict
              ? 'Activities changed in another session. The latest database version was reloaded.'
              : requestError instanceof Error ? requestError.message : 'Unable to save activities');
          }
          break;
        }
      }
    } finally {
      savingRef.current = false;
      if (mountedRef.current) setIsSaving(false);
      if (pendingRef.current) void persistPending();
    }
  }, [reload, synchronize]);

  const saveActivities = useCallback((next: Activity[]) => {
    setActivities(next);
    pendingRef.current = next;
    void persistPending();
  }, [persistPending]);

  const clearAllActivities = useCallback(async (expectedRevision:number,idempotencyKey:string) => {
    if (savingRef.current || pendingRef.current) throw new Error('Wait for activity changes to finish saving before clearing the tracker');
    savingRef.current=true;setIsSaving(true);
    try {
      const result=await apiFetch('/api/v1/activities/clear',{method:'POST',headers:{'Idempotency-Key':idempotencyKey},
        body:JSON.stringify({confirm:'CLEAR',expectedRevision})}) as {cleared:number;revision:number};
      serverRef.current=[];pendingRef.current=null;setActivities([]);setError(null);
      await reload();
      return result;
    } catch(error) {await reload();throw error;}
    finally {savingRef.current=false;setIsSaving(false);}
  },[reload]);

  // Drawer updates use single-record commands, preserving scoped editor access
  // without requiring a bulk permission or reconciling the entire snapshot.
  const updateActivity = useCallback(async (id: string, patch: Partial<Activity>) => {
    if (savingRef.current) throw new Error('An activity update is already being saved');
    const current = serverRef.current.find(activity => activity.id === id);
    if (!current) throw new Error('Activity is no longer in your accessible scope');
    savingRef.current = true;
    setIsSaving(true);
    try {
      const transition = patch.status !== undefined;
      const result = await apiFetch(`/api/v1/activities/${encodeURIComponent(id)}${transition ? '/transitions' : ''}`, {
        method: transition ? 'POST' : 'PATCH', headers: headers(),
        body: JSON.stringify(transition
          ? { expectedVersion: current.version || 1, targetStatus: patch.status, reason: 'Updated from Executive Overview' }
          : { expectedVersion: current.version || 1, patch }),
      }) as MutationResult;
      serverRef.current = serverRef.current.map(activity => activity.id === id ? result.task : activity);
      setActivities(serverRef.current);
      setError(null);
    } catch (requestError) {
      await reload();
      setError(requestError instanceof Error ? requestError.message : 'Unable to update activity');
      throw requestError;
    } finally {
      savingRef.current = false;
      setIsSaving(false);
    }
  }, [reload]);

  const bulkUpdateActivities = useCallback(async (idsValue: Iterable<string>, patch: Partial<Activity>) => {
    const ids = Array.from(new Set(Array.from(idsValue)));
    if (!ids.length) return;
    const currentById = new Map(serverRef.current.map((task) => [task.id, task]));
    setIsSaving(true);
    try {
      const status = patch.status;
      const request = status
        ? { operation: 'transition', taskIds: ids, targetStatus: status, reason: patch.note || 'Bulk update from Activity Tracker' }
        : { operation: 'update', taskIds: ids, patch: Object.fromEntries(PATCH_FIELDS.filter((field) => field in patch).map((field) => [field, patch[field]])) };
      const result = await apiFetch('/api/v1/activities/bulk', {
        method: 'POST', headers: headers(), body: JSON.stringify({
          expectedVersions: Object.fromEntries(ids.map((id) => [id, currentById.get(id)?.version || 1])), request,
        }),
      }) as { tasks: Activity[] };
      const changed = new Map(result.tasks.map((task) => [task.id, task]));
      serverRef.current = serverRef.current.map((task) => changed.get(task.id) || task);
      setActivities(serverRef.current);
      setError(null);
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : 'Unable to update selected activities');
      await reload();
    } finally {
      setIsSaving(false);
    }
  }, [reload]);

  const deleteActivities = useCallback(async (idsValue: Iterable<string>) => {
    const ids = Array.from(new Set(Array.from(idsValue)));
    if (!ids.length) return;
    const desired = activities.filter((task) => !ids.includes(task.id));
    saveActivities(desired);
  }, [activities, saveActivities]);

  const bulkMoveActivities = useCallback(async (idsValue: Iterable<string>, direction: 'indent' | 'outdent') => {
    const ids = Array.from(new Set(Array.from(idsValue)));
    if (!ids.length) return;
    const currentById = new Map(serverRef.current.map((task) => [task.id, task]));
    setIsSaving(true);
    try {
      await apiFetch('/api/v1/activities/bulk', {
        method: 'POST', headers: headers(), body: JSON.stringify({
          expectedVersions: Object.fromEntries(ids.map((id) => [id, currentById.get(id)?.version || 1])),
          request: { operation: 'move', taskIds: ids, direction },
        }),
      });
      await reload();
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : 'Unable to move selected activities');
      await reload();
    } finally {
      setIsSaving(false);
    }
  }, [reload]);

  useEffect(() => {
    mountedRef.current = true;
    void reload();
    return () => { mountedRef.current = false; };
  }, [reload]);

  return {
    activities,
    saveActivities,
    updateActivity,
    bulkUpdateActivities,
    bulkMoveActivities,
    deleteActivities,
    clearAllActivities,
    reload,
    loadedAt,
    isLoading,
    isSaving,
    error,
    capabilities,
    clearError: () => setError(null),
  };
}
